
import { Link, NavLink, useLocation } from 'react-router-dom';
import { ShoppingCart, Menu, X, LogOut, Shield, ArrowLeft, Package } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Logo from './Logo';
import { useState, useEffect } from 'react';
import { useCart } from '@/context/CartContext';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import UserProfileDropdown from './UserProfileDropdown';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Sheet, SheetContent, SheetHeader } from '@/components/ui/sheet';
import { OrderLookupSheet } from './OrderLookupSheet';

const navLinks = [
  { href: '/shop', label: 'Shop' },
  { href: '/plant-care', label: 'Plant Care' },
  { href: '/about', label: 'About Us' },
];

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isOrderLookupOpen, setIsOrderLookupOpen] = useState(false);
  const [firstName, setFirstName] = useState<string>('');
  const { cartCount, openCartDrawer } = useCart();
  const { session, isAdmin, user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Check if we're on a product detail page
  const isProductPage = location.pathname.startsWith('/product/');

  useEffect(() => {
    const fetchProfile = async () => {
      if (user?.id) {
        const { data } = await supabase
          .from('profiles')
          .select('first_name')
          .eq('user_id', user.id)
          .maybeSingle();
        
        if (data?.first_name) {
          setFirstName(data.first_name);
        }
      }
    };
    fetchProfile();
  }, [user]);

  const handleLogout = async () => {
    try {
      await logout();
      toast.success('Logged out successfully');
      setIsMenuOpen(false);
      navigate('/');
    } catch (error: any) {
      toast.error(`Error logging out: ${error.message}`);
    }
  };

  const handleOpenOrderLookup = () => {
    setIsMenuOpen(false);
    setIsOrderLookupOpen(true);
  };

  return (
    <>
      <header className="bg-background border-b border-border">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 gap-4">
            {/* Left: Hamburger Menu or Back Button */}
            <div className="flex items-center">
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => isProductPage ? navigate(-1) : setIsMenuOpen(!isMenuOpen)}
                className="hover:bg-muted/50"
              >
                {isProductPage ? (
                  <ArrowLeft className="h-6 w-6" />
                ) : (
                  <Menu className="h-6 w-6" />
                )}
                <span className="sr-only">{isProductPage ? 'Go back' : 'Toggle menu'}</span>
              </Button>
            </div>

            {/* Center: Logo */}
            <div className="absolute left-1/2 transform -translate-x-[55%]">
              <Logo onLogoClick={() => setIsMenuOpen(false)} />
            </div>

            {/* Right: Cart & User Profile */}
            <div className="flex items-center gap-3">
              <Button variant="ghost" onClick={() => openCartDrawer()} className="relative h-[24px] w-[24px] md:h-[28px] md:w-[28px] hover:bg-transparent p-0 [&_svg]:!w-[24px] [&_svg]:!h-[24px] md:[&_svg]:!w-[28px] md:[&_svg]:!h-[28px] mr-[5.2px] md:mr-5">
                <ShoppingCart strokeWidth={1.5} />
                {cartCount > 0 && (
                  <div className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center text-[11px] font-medium bg-foreground text-background rounded-full">
                    {cartCount}
                  </div>
                )}
                <span className="sr-only">Shopping Cart</span>
              </Button>
              {session && (
                <div className="flex flex-col items-center">
                  <UserProfileDropdown user={user} />
                  <span className="text-[10px] text-muted-foreground whitespace-nowrap">
                    Hi {firstName || 'Guest'}!
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
        <Sheet open={isMenuOpen && !isProductPage} onOpenChange={setIsMenuOpen}>
          <SheetContent side="left" className="w-[280px] p-0">
            <SheetHeader className="px-6 py-5 border-b border-border">
              <div className="flex items-center justify-between">
                <Logo onLogoClick={() => setIsMenuOpen(false)} />
                <Button variant="ghost" size="icon" onClick={() => setIsMenuOpen(false)} className="h-9 w-9 -mr-2">
                  <X className="h-5 w-5" />
                </Button>
              </div>
            </SheetHeader>
            
            <nav className="flex flex-col p-4 gap-1">
              {navLinks.map((link) => (
                <NavLink
                  key={link.href}
                  to={link.href}
                  onClick={() => setIsMenuOpen(false)}
                  className={({ isActive }) =>
                    `px-4 py-3 text-base font-medium transition-colors hover:bg-muted rounded-md ${
                      isActive ? 'text-primary bg-muted' : 'text-foreground'
                    }`
                  }
                >
                  {link.label}
                </NavLink>
              ))}

              {/* Track Order Option */}
              <button
                onClick={handleOpenOrderLookup}
                className="flex items-center gap-2 px-4 py-3 text-base font-medium transition-colors hover:bg-muted rounded-md text-foreground text-left"
              >
                <Package className="h-5 w-5" />
                Track Order
              </button>
              
              {isAdmin && (
                <NavLink
                  to="/admin"
                  onClick={() => setIsMenuOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-2 px-4 py-3 text-base font-medium transition-colors hover:bg-muted rounded-md ${
                      isActive ? 'text-primary bg-muted' : 'text-foreground'
                    }`
                  }
                >
                  <Shield className="h-5 w-5" /> Admin
                </NavLink>
              )}

              <div className="border-t border-border my-3"></div>

              {!session && (
                <Button asChild onClick={() => setIsMenuOpen(false)} className="mx-2">
                  <Link to="/auth">Login</Link>
                </Button>
              )}
            </nav>
          </SheetContent>
        </Sheet>
      </header>

      {/* Order Lookup Sheet */}
      <OrderLookupSheet 
        open={isOrderLookupOpen} 
        onOpenChange={setIsOrderLookupOpen} 
      />
    </>
  );
};

export default Header;
